'use client';

import { Button } from '@/components/Button';
import { FormCheckbox } from '@/components/FormCheckbox';
import { FormInput } from '@/components/FormInput';
import { InternalLink } from '@/components/InternalLink';
import { useUserSignUp } from '@/hooks/useUserQuery';
import { useTranslations } from 'next-intl';

export const SignUpForm = () => {
  const tForms = useTranslations('forms');
  const tSignUp = useTranslations('sign-up');
  const {
    isLoading,
    emailAlreadyExists,
    formErrors,
    registerFormModel,
    onSubmit,
  } = useUserSignUp();

  return (
    <form className="space-y-6" onSubmit={onSubmit}>
      {/* Personal Information */}
      <div className="mt-8 flex flex-col space-y-4 sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-left text-lg font-bold leading-9 tracking-tight text-gray-900">
          {tSignUp('label-headline-personal-data')}
        </h2>
        <FormInput
          {...registerFormModel('firstName')}
          data-testid="signup-firstname"
          label={tForms('label-firstname')}
          errorMessage={formErrors.firstName?.message}
        />
        <FormInput
          {...registerFormModel('lastName')}
          data-testid="signup-lastname"
          label={tForms('label-lastname')}
          errorMessage={formErrors.lastName?.message}
        />
        <FormInput
          {...registerFormModel('email')}
          data-testid="signup-email"
          label={tForms('label-email')}
          type="email"
          errorMessage={
            emailAlreadyExists
              ? tForms('error-email-already-exists')
              : formErrors.email?.message
          }
        />
        <FormInput
          {...registerFormModel('password')}
          data-testid="signup-password"
          label={tForms('label-password')}
          type="password"
          errorMessage={formErrors.password?.message}
        />
        <FormInput
          {...registerFormModel('passwordConfirmation')}
          data-testid="signup-password-confirmation"
          label={tForms('label-password')}
          type="password"
          errorMessage={formErrors.passwordConfirmation?.message}
        />
        <p className="mt-10 text-left text-xs text-gray-500">
          {tForms('label-password-strength')}
        </p>
      </div>
      {/* Outstation */}
      <div className="mt-8 space-y-4 sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="text-left text-lg font-bold leading-9 tracking-tight text-gray-900">
          Informationen der Außenstelle
        </h2>
        <FormInput
          {...registerFormModel('kitchenName')}
          data-testid="signup-kitchen"
          label={tForms('label-kitchen')}
          errorMessage={formErrors.kitchenName?.message}
        />
        <FormInput
          {...registerFormModel('branchOfficeId')}
          data-testid="signup-branch-office-id"
          label={tForms('label-branch-office-id')}
          errorMessage={formErrors.branchOfficeId?.message}
        />
        <FormInput
          {...registerFormModel('supervisorName')}
          data-testid="signup-supervisor-name"
          label={tForms('label-supervisor-name')}
          errorMessage={formErrors.supervisorName?.message}
        />
        <FormCheckbox
          {...registerFormModel('termsAndConditions')}
          data-testid="signup-terms"
          label={tForms('label-terms-and-conditions')}
          errorMessage={formErrors.termsAndConditions?.message}
        />
      </div>
      {/* centered container */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Button
          type="submit"
          isLoading={isLoading}
          className="w-full"
          data-testid="signup-submit"
        >
          {tSignUp('label-sign-up')}
        </Button>
      </div>
      <div className="mt-10 flex items-center justify-center">
        <div className="text-sm leading-6">
          <InternalLink href={'/sign-in'} color="text">
            {tSignUp('label-already-registered')}
          </InternalLink>
        </div>
      </div>
      {/*
        Honeypot fields - Multiple techniques to catch bots:
        1. Fields positioned in flow but clipped/invisible (not offscreen)
        2. Attractive field names bots target (phone, url, fax)
        3. Realistic labels and structure
        4. No obvious CSS patterns like -9999px or display:none
      */}
      <div className="mt-8 space-y-4 sm:mx-auto sm:w-full sm:max-w-md">
        {/* Visually hidden using clip-path and opacity - undetectable by simple CSS checks */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {/* DO NOT add name attribute - it breaks react-hook-form tracking */}
          <label htmlFor="emailConfirm">Phone Number</label>
          <input
            id="emailConfirm"
            type="tel"
            tabIndex={-1}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            {...registerFormModel('emailConfirm')}
          />
          <label htmlFor="companyWebsite">Company URL</label>
          <input
            id="companyWebsite"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            {...registerFormModel('companyWebsite')}
          />
        </div>
      </div>
    </form>
  );
};
